/**
 * @typedef {{
 *    palette: string
 *    independent_scale: boolean
 *    show_contextual_timeline: boolean
 * }} TemporlaEvolutionVizOptions
 */

import { scaleSequential } from 'd3-scale'
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react'
import { PALETTE } from '../../../consts'
import { saveAs } from 'file-saver'
import classNames from 'classnames'
import ItemsWidget from '../../Widget/ItemsWidget'
import FilterFieldPickerWidget from '../../Widget/FilterFieldPickerWidget'
import FilterFacetFieldsWidget from '../../Widget/FilterFacetFieldsWidget'
import saveSvgAsImage, { filterRecords, getInLang } from '../../../utils'
import FilterTimeFieldsWidget from '../../Widget/FilterTimeFieldsWidget'
import EvolutionChart from '../EvolutionChart'
import ContextualTimeline from '../ContextualTimeline'
import styles from './TemporlaEvolutionViz.module.scss'
import { useVizFilters } from '../../../hooks/filters'
import { useAuthState } from 'use-eazy-auth'
import { select } from 'd3-selection'
import logo from '../../../assets/PalREAD_watermark_low.png'

/**
 * @param {{
 *  records: any[]
 *  meta: { fields: Record<string, import("../../../hooks/schema").OmekaFieldType> }
 *  query: QueryEditorState
 *  options: TemporlaEvolutionVizOptions
 *  setOptions?(options: TemporlaEvolutionVizOptions | ((options: TemporlaEvolutionVizOptions) => TemporlaEvolutionVizOptions)): void
 *  language: 'en' | 'ar'
 * }} props
 */
function TemporlaEvolutionViz(
  {
    records,
    options,
    meta,
    language,
    query,
    title,
    setOptions,
    timelineData,
    viewer,
  },
  ref
) {
  const [
    { filters, filterableFields },
    { setFilters, addFilter, removeFilter },
  ] = useVizFilters(meta.fields, options, setOptions)

  const filteredRecords = useMemo(
    () => filterRecords(records, filters),
    [records, filters]
  )

  const containerRef = useRef()

  useImperativeHandle(ref, () => ({
    exportViz: () => {
      const svgElement = containerRef.current.querySelector('svg#viz')
      const img = select(svgElement)
        .append('image')
        .attr('href', logo)
        .attr('height', 40)
        .attr('width', 'auto')
        .attr('x', 10)
        .attr('y', select(svgElement).attr('height') - 50)
        .style('opacity', 0.5)

      saveSvgAsImage(
        svgElement,
        getInLang(title, language)
          ? getInLang(title, language) + '.svg'
          : 'evolution.svg'
      )
      img.remove()
    },
  }))

  const { authenticated } = useAuthState()

  return (
    <div className="w-100 h-100 d-flex bg-background-pr" ref={containerRef}>
      <div className="flex-grow-1 flex-shrink-1 d-flex flex-column pe-3">
        <div className={classNames(styles.evolutionContainer)}>
          {records.length > 0 && (
            <EvolutionChart
              records={filteredRecords}
              options={options}
              query={query}
              language={language}
            ></EvolutionChart>
          )}
        </div>
        <div className="flex-grow-0 flex-shrink-0">
          <FilterTimeFieldsWidget
            meta={meta}
            records={records}
            filters={filters}
            onFiltersChange={setFilters}
            onFilterRemoved={setOptions ? removeFilter : null}
          />
          {options.show_contextual_timeline && (
            <ContextualTimeline
              data={timelineData}
              filters={filters}
            ></ContextualTimeline>
          )}
        </div>
      </div>
      <div
        className={classNames(styles.widgetContainerRight, {
          [styles.viewer]: viewer,
        })}
      >
        {setOptions && (
          <FilterFieldPickerWidget
            fields={filterableFields}
            filters={filters}
            onFilterPicked={addFilter}
            onFiltersChange={setFilters}
          />
        )}
        <ItemsWidget
          records={records}
          filteredRecords={filteredRecords}
          language={language}
        />
        <FilterFacetFieldsWidget
          meta={meta}
          records={records}
          filters={filters}
          onFiltersChange={setFilters}
          onFilterRemoved={setOptions ? removeFilter : null}
        />
      </div>
    </div>
  )
}

export default memo(forwardRef(TemporlaEvolutionViz))
